<template>
  <div class="container-fluid" id="devicePage">
    <!-- Page title -->
    <page-title
      :canDownload="false"
      :title="this.$t('device_sessions')"
      iconClasses="fas fa-tablet-alt"
      :isDataLoading="isDataLoading"
      :isTimelineDisplayed="true"
    />

    <!-- Form controls -->
    <form-controls
      v-if="controlsConfig.length > 0"
      :controls="controlsConfig"
      v-on:updateControl="handleChange"
      v-on:setDefaultValues="setFormControlsDefault"
      :displayConfig="{ toggleableGroups: ['filters'] }"
    />

    <!-- Session's count indicator label -->
    <div class="container-fluid comment">
      <div class="row justify-content-md-center mt-4">
        <h5>
          <strong>{{ dataset && dataset.totalCount ? dataset.totalCount : 0 }}</strong>
          {{ $t('sessions_found') }}
          {{
            String($t('timeline.from')).toLowerCase() +
              ' ' +
              this.period[0] +
              ' ' +
              String($t('timeline.to')).toLowerCase() +
              ' ' +
              this.period[1]
          }}
        </h5>
      </div>
    </div>

    <!--  Nav tabs -->
    <div class="container col-sm-12 mt-3 mb-4" v-if="dataset">
      <nav>
        <div class="nav nav-tabs justify-content-center" id="nav-tab" role="tablist">
          <a
            class="nav-item nav-link active"
            id="nav-sessions-tab"
            data-toggle="tab"
            href="#nav-sessions"
            role="tab"
            aria-controls="nav-sessions"
            aria-selected="true"
            >{{ $t('sessions') }}</a
          >
          <a
            class="nav-item nav-link"
            id="nav-battery-level-tab"
            data-toggle="tab"
            href="#nav-battery-level"
            role="tab"
            aria-controls="nav-battery-level"
            aria-selected="false"
            v-if="hasBatteryLevels"
            >{{ $t('battery.levels') }}</a
          >
          <a
            class="nav-item nav-link"
            id="nav-battery-temperature-tab"
            data-toggle="tab"
            href="#nav-battery-temperature"
            role="tab"
            aria-controls="nav-battery-temperature"
            aria-selected="false"
            v-if="hasBatteryTemperatures"
            >{{ $t('battery.temperatures') }}</a
          >
        </div>
      </nav>

      <div class="tab-content mb-3" id="nav-tabContent">
        <!-- Session's table -->
        <div
          class="tab-pane fade show active"
          id="nav-sessions"
          role="tabpanel"
          aria-labelledby="nav-sessions-tab"
        >
          <div class="container-fluid" v-if="!isMobileScreen">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col" class="text-center">{{ $t('session.id') }}</th>
                  <th scope="col" class="text-center">{{ $t('serial') }}</th>
                  <th scope="col" class="text-center">{{ $t('site.title') }}</th>
                  <th scope="col" class="text-center">{{ $t('start') }}</th>
                  <th scope="col" class="text-center">{{ $t('end') }}</th>
                  <th scope="col" class="text-center">{{ $t('session.duration') }}</th>
                  <th scope="col" class="text-center">{{ $t('filter.language') }}</th>
                  <th scope="col" class="text-center">{{ $t('chart.data_value.is_crash') }} ?</th>
                  <th scope="col" class="text-center">Test ?</th>
                  <th scope="col" class="text-center">Status</th>
                </tr>
              </thead>
              <tbody v-if="hasSessions">
                <tr v-for="(session, index) in dataset.sessions" :key="index">
                  <th scope="row" class="text-center">
                    <a :href="'/session/' + session.session_id" target="_blank">{{
                      session.session_id
                    }}</a>
                  </th>
                  <td class="text-center">
                    <a :href="'/sessions/device/' + session.device_serial" target="_blank">{{
                      session.device_serial
                    }}</a>
                  </td>
                  <td class="text-center">{{ getLocalizedSiteName(session.site_location) }}</td>
                  <td class="text-center">{{ session.start_time }}</td>
                  <td class="text-center">{{ session.end_time }}</td>
                  <td class="text-center">{{ session.duration }} min</td>
                  <td class="text-center">{{ $t('language.' + session.language) }}</td>
                  <td class="text-center">{{ session.is_crash ? $t('yes') : $t('no') }}</td>
                  <td class="text-center">{{ session.is_test ? $t('yes') : $t('no') }}</td>
                  <td class="text-center">{{ session.state }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="isMobileScreen">
            <div
              class="container-fluid rounded p-3 mb-3 btn-chart"
              v-for="(session, index) in dataset.sessions"
              :key="index"
            >
              <div class="row">
                <div class="col is-4">
                  {{ $t('session.id') }}
                </div>
                <div class="col is-8">
                  <a :href="`/session/${session.session_id}`" target="_blank">
                    {{ session.session_id }}
                  </a>
                </div>
              </div>
              <div class="row">
                <div class="col is-4">
                  {{ $t('serial') }}
                </div>
                <div class="col is-8">
                  <a :href="`/sessions/device/${session.device_serial}`" target="_blank">{{
                    session.device_serial
                  }}</a>
                </div>
              </div>
              <div class="row">
                <div class="col is-4">
                  {{ $t('start') }}
                </div>
                <div class="col is-8">
                  {{ session.start_time }}
                </div>
              </div>
              <div class="row">
                <div class="col is-4">
                  {{ $t('end') }}
                </div>
                <div class="col is-8">
                  {{ session.end_time }}
                </div>
              </div>
              <div class="row">
                <div class="col is-4">
                  {{ $t('session.duration') }}
                </div>
                <div class="col is-8">
                  {{ session.duration }}
                </div>
              </div>
              <div class="row">
                <div class="col is-4">
                  {{ $t('filter.language') }}
                </div>
                <div class="col is-8">
                  {{ $t(`language.${session.language}`) }}
                </div>
              </div>
              <div class="row">
                <div class="col is-4">
                  {{ $t('chart.data_value.is_crash') }}
                </div>
                <div class="col is-8">
                  {{ session.is_crash ? $t('yes') : $t('no') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Battery levels chart -->
        <div
          class="tab-pane fade"
          id="nav-battery-level"
          role="tabpanel"
          aria-labelledby="nav-battery-level-tab"
          v-if="hasBatteryLevels"
        >
          <div class="row justify-content-center">
            <div class="col-sm-12">
              <battery-exploitation-chart
                :dataset="dataset.battery_levels"
                :site="dataset.sessions[0] ? dataset.sessions[0].site_location : 'not defined'"
                :chartId="batteryLevelChartId"
                :title="$t('battery.infos_levels', [dataset.battery_levels.levels.length])"
                :xTitle="timeTitle"
                :yTitle="batteryLevelTitle"
                @update="onChartDataUpdate"
              ></battery-exploitation-chart>
              <chart-config-menu
                :chart="dataset.battery_levels"
                :chartsData="chartsData"
                @showModal="() => {}"
              ></chart-config-menu>
            </div>
          </div>
        </div>
        <!-- Battery temperatures chart -->
        <div
          class="tab-pane fade"
          id="nav-battery-temperature"
          role="tabpanel"
          aria-labelledby="nav-battery-temperature-tab"
          v-if="hasBatteryTemperatures"
        >
          <div class="row justify-content-center">
            <div class="col-sm-12">
              <battery-exploitation-chart
                :dataset="dataset.battery_temperatures"
                :site="dataset.sessions[0].site_location"
                :chartId="batteryTemperatureChartId"
                :title="
                  $t('battery.infos_temperatures', [dataset.battery_temperatures.levels.length])
                "
                :xTitle="timeTitle"
                :yTitle="batteryTemperatureTitle"
              ></battery-exploitation-chart>
              <chart-config-menu
                :chart="dataset.battery_temperatures"
                :chartsData="chartsData"
                @showModal="() => {}"
              ></chart-config-menu>
            </div>
          </div>
        </div>

        <!-- Download button -->
        <div class="row mt-2" v-if="downloaded && dataset">
          <div class="col">
            <button
              class="btn btn-chart mb-3"
              @click="downloadLogs"
              v-if="dataset.totalCount <= 2000"
              :disabled="isDataLoading"
            >
              {{ $t('download.sessions') }}
              <i class="ml-2 fas fa-download"></i>
            </button>
            <button
              class="btn btn-chart mb-3"
              href="#"
              data-toggle="modal"
              data-target="#confirmDownloadLogs"
              v-if="dataset.totalCount > 2000"
              :disabled="isDataLoading"
            >
              {{ $t('download.sessions') }}
              <i class="ml-2 fas fa-download"></i>
            </button>
          </div>
          <div class="col">
            <nav aria-label="Page comment navigation">
              <paginate
                v-model="currentPage"
                :page-count="Number(dataset.totalPage)"
                :page-range="3"
                :margin-pages="2"
                :click-handler="onSessionsPageChange"
                :prev-text="'&laquo;'"
                :next-text="'&raquo;'"
                :container-class="'pagination justify-content-end'"
                :page-class="'page-item'"
                :page-link-class="'page-link'"
                :prev-link-class="'page-link'"
                :next-link-class="'page-link'"
              ></paginate>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal use to confirm logs download -->
    <div
      class="modal fade"
      id="confirmDownloadLogs"
      tabindex="-1"
      role="dialog"
      aria-labelledby="confirmDownloadLogsLabel"
      aria-hidden="true"
      v-if="dataset"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="confirmDownloadLogsLabel">
              {{ $t('download.logs_confirmation') }}
            </h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <label v-html="$t('download.logs_confirmation_infos', [dataset.totalCount])"> </label>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">
              {{ $t('cancel') }}
            </button>
            <button type="button" class="btn btn-danger" data-dismiss="modal" @click="downloadLogs">
              {{ $t('confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import moment from 'moment'
import { utilsMixin, sitesMixin, chartMixin } from '../utils/mixins'
import VueScrollTo from 'vue-scrollto'

//Scroll to
Vue.use(VueScrollTo, {
  container: 'body',
  duration: 500,
  easing: 'ease',
  offset: -100,
  force: true,
  cancelable: true,
  onStart: false,
  onDone: false,
  onCancel: false,
  x: false,
  y: true,
})

export default {
  name: 'Device',
  dependencies: ['dataService'],
  mixins: [utilsMixin, sitesMixin, chartMixin],
  props: ['period'],
  data() {
    return {
      downloaded: false,
      isLoading: false,
      dataset: null,
      chartsData: {},
      timelineId: 'timeline',
      deviceForm: {
        device_serial: this.$route.params.device ? this.$route.params.device : '',
        order: this.$route.params.order ? this.$route.params.order : 'start_time-desc',
      },
      formControl: {},
      controlsConfig: [],
      timeTitle: this.$t('filter.duration') + ' (minutes)',
      batteryLevelChartId: 'sessionBatteryLevelChart',
      batteryTemperatureChartId: 'sessionBatteryTemperatureChart',
      batteryLevelTitle: this.$t('battery.levels') + ' (%)',
      batteryTemperatureTitle: this.$t('battery.temperatures') + ' (°C)',
      currentPage: this.$route.params.page ? this.$route.params.page : 1,
    }
  },
  watch: {
    period: {
      handler() {
        this.fetchSessionsFromDeviceSerial()
      },
      immediate: true,
    },
  },
  mounted() {
    this.controlsConfig = this.generateFormControlsConfig()
    this.formControl['page'] = this.currentPage
  },
  beforeUnmount() {
    this.dataService.cancelDataRequest(this.currentRequestId)
  },
  components: {
    'form-controls': () => import('./utils/FormControls'),
    'page-title': () => import('./utils/PageTitle'),
    'chart-config-menu': () => import('./utils/ChartConfigMenu'),
    'battery-exploitation-chart': () => import('./charts/exploitation/BatteryExploitationChart'),
  },
  computed: {
    hasSessions: function() {
      return (
        this.downloaded && this.dataset && this.dataset.sessions && this.dataset.sessions.length > 0
      )
    },
    hasBatteryLevels: function() {
      return (
        this.dataset &&
        this.dataset &&
        this.dataset.battery_levels.levels.length > 0 &&
        this.hasSessions
      )
    },
    hasBatteryTemperatures: function() {
      return (
        this.dataset &&
        this.dataset.battery_temperatures &&
        this.dataset.battery_temperatures.levels.length > 0 &&
        this.hasSessions
      )
    },
  },
  methods: {
    onSessionsPageChange() {
      this.fetchSessionsFromDeviceSerial()
      VueScrollTo.scrollTo('#devicePage')
    },
    fetchSessionsFromDeviceSerial: function() {
      if (
        (Object.keys(this.formControl).length == 0 ||
          this.formControl.device_serial == '' ||
          !this.formControl.device_serial) &&
        this.formControl.site == 'all'
      ) {
        return
      }
      this.isLoading = true

      this.start = moment(this.period[0], this.dateFormat).format('YYYY-MM-DD')
      this.end = moment(this.period[1], this.dateFormat).format('YYYY-MM-DD')

      window.history.pushState(
        null,
        null,
        '/sessions/device/' +
          (this.formControl.device_serial && this.formControl.device_serial.trim() !== ''
            ? this.formControl.device_serial
            : 'all') +
          '/session/' +
          (this.formControl.session_id && this.formControl.session_id.trim() !== ''
            ? this.formControl.session_id
            : 'all') +
          '/order/' +
          this.formControl.order +
          '/site/' +
          this.formControl.site_location +
          '/language/' +
          this.formControl.language +
          '/histopad_rating/' +
          this.formControl.histopad_rating +
          '/visit_rating/' +
          this.formControl.visit_rating +
          '/collected_treasure_count/' +
          this.formControl.collected_treasure_count +
          '/is_crash/' +
          this.formControl.is_crash +
          '/state/' +
          this.formControl.state +
          '/start/' +
          this.start +
          '/end/' +
          this.end,
      )

      this.formControl['page'] = this.currentPage

      this.sanitizeInputText('session_id')
      this.sanitizeInputText('device_serial')

      this.requestCount++

      this.dataService
        .getDeviceSessions(this.formControl, this.start, this.end, this.currentRequestId)
        .then(data => {
          this.dataset = data.result
          this.processBatteryLevels()
          this.downloaded = true
          this.requestCount--
        })
        .catch(error => {
          if (error.message !== 'cancel') {
            console.error('Error when retrieving sessions:', error)
            this.downloaded = false
          }
          this.requestCount--
        })

      this.currentRequestId = `sessions${JSON.stringify(this.formControl)}${this.start}${this.end}`
    },
    processBatteryLevels: function() {
      this.dataset['battery_levels'] = {
        type: 'level_points',
        levels: [],
        is_crash: false,
      }
      this.dataset['battery_temperatures'] = {
        type: 'temperature_points',
        levels: [],
        is_crash: false,
      }

      this.dataset.sessions.forEach(session => {
        if (session.battery_information && session.battery_information.level_points) {
          this.dataset['battery_levels'].levels.push({
            session_id: session.session_id,
            device_serial: session.device_serial,
            values: session.battery_information.level_points,
            is_crash: session.is_crash,
          })
        }
        if (session.battery_information && session.battery_information.temperature_points) {
          this.dataset['battery_temperatures'].levels.push({
            session_id: session.session_id,
            device_serial: session.device_serial,
            values: session.battery_information.temperature_points,
            is_crash: session.is_crash,
          })
        }
      })
    },
    sanitizeInputText: function(key) {
      if (this.formControl[key] && this.formControl[key] !== '') {
        if (this.formControl[key].indexOf(',') > -1) {
          let splits = this.formControl[key].split(',')
          let sanitized = ''
          splits.forEach((split, index) => {
            split = split.trim()
            if (split !== '') {
              if (index > 0) {
                sanitized += ','
              }
              sanitized += split
            }
          })
          this.formControl[key] = sanitized
        } else {
          this.formControl[key] = this.formControl[key].trim()
        }
      }
    },
    onChartDataUpdate: function(chartData) {
      this.chartsData[chartData.type] = chartData
    },
    handleChange: function(change) {
      this.formControl[change.vModel] = change.value
      if (change.vModel == 'site') {
        this.controlsConfig = this.generateFormControlsConfig()
      }
      this.fetchSessionsFromDeviceSerial()
    },
    generateFormControlsConfig: function() {
      let config = [
        {
          label: this.$t('session.id'),
          type: 'text',
          vModel: 'session_id',
          group: 'display',
          default:
            this.$route.params.session && this.$route.params.session !== 'all'
              ? this.$route.params.session
              : '',
        },
        {
          label: this.$t('device_placeholder'),
          type: 'text',
          vModel: 'device_serial',
          group: 'display',
          default:
            this.$route.params.device && this.$route.params.device !== 'all'
              ? this.$route.params.device
              : '',
        },
        {
          label: this.$t('site.title'),
          type: 'select',
          vModel: 'site_location',
          group: 'display',
          default: this.checkDefaultSite(this.$route.params.site),
          options: [
            {
              value: 'all',
              label: this.$t('select_all'),
            },
            ...this.getCurrentUser.sites.map(site => ({
              value: site.site,
              label: site.name,
            })),
          ],
        },
        {
          label: this.$t('sort_by'),
          type: 'select',
          vModel: 'order',
          group: 'display',
          default: this.$route.params.order ? this.$route.params.order : 'start_time-desc',
          options: [
            {
              value: 'start_time-desc',
              label: this.$t('sort.by_date_desc'),
            },
            {
              value: 'start_time-asc',
              label: this.$t('sort.by_date_asc'),
            },
            {
              value: 'device_serial-desc',
              label: this.$t('sort.by_device_serial_desc'),
            },
            {
              value: 'device_serial-asc',
              label: this.$t('sort.by_device_serial_asc'),
            },
            {
              value: 'site_id-desc',
              label: this.$t('sort.by_site_id_desc'),
            },
            {
              value: 'site_id-asc',
              label: this.$t('sort.by_site_id_asc'),
            },
          ],
        },

        {
          label: this.$t('filter.by_language'),
          type: 'select',
          vModel: 'language',
          default: this.$route.params.language ? this.$route.params.language : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            ...this.configFilterHierarchy.filter_values.language.map(lang => ({
              value: lang,
              label: this.$t(`language.${lang}`),
            })),
          ],
        },
        {
          label: this.$t('filter.by_histopad_rating'),
          type: 'select',
          vModel: 'histopad_rating',
          default: this.$route.params.histopad_rating ? this.$route.params.histopad_rating : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: '0', label: '0' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' },
          ],
        },
        {
          label: this.$t('filter.by_visit_rating'),
          type: 'select',
          vModel: 'visit_rating',
          default: this.$route.params.visit_rating ? this.$route.params.visit_rating : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: '0', label: '0' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' },
          ],
        },
        {
          label: this.$t('filter.collected_treasure_count'),
          type: 'select',
          vModel: 'collected_treasure_count',
          default: this.$route.params.collected_treasure_count
            ? this.$route.params.collected_treasure_count
            : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            ...this.configFilterHierarchy.filter_values.collected_treasure_count.map(treasure => ({
              value: treasure,
              label: treasure,
            })),
          ],
        },
        {
          label: this.$t('chart.data_value.is_crash'),
          type: 'select',
          vModel: 'is_crash',
          default: this.$route.params.is_crash ? this.$route.params.is_crash : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: 1, label: this.$t('yes') },
            { value: 0, label: this.$t('no') },
          ],
        },
        {
          label: 'Test ?',
          type: 'select',
          vModel: 'is_test',
          default: this.$route.params.is_test ? this.$route.params.is_test : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: 1, label: this.$t('yes') },
            { value: 0, label: this.$t('no') },
          ],
        },
        {
          label: 'Status',
          type: 'select',
          vModel: 'state',
          default: this.$route.params.state ? this.$route.params.state : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: 'OK', label: this.$t('state.OK') },
            { value: 'KO', label: this.$t('state.KO') },
            { value: 'PENDING', label: this.$t('state.PENDING') },
          ],
        },
      ]

      return config
    },
    downloadLogs: function() {
      this.requestCount++
      VueScrollTo.scrollTo('#devicePage')
      this.dataService
        .downloadLogs(
          this.formControl,
          this.dataset.totalCount,
          this.getCurrentUser.email,
          this.start,
          this.end,
        )
        .then(data => {
          if (data.type == 'application/zip') {
            const url = window.URL.createObjectURL(new Blob([data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `Logs_${moment().format('YYYY-MM-DD_HHmmss')}.zip`)
            document.body.appendChild(link)
            link.click()
            this.requestCount--
          } else {
            this.showToast(this.$t('download.logs_email', [this.getCurrentUser.email]))
            this.requestCount--
          }
        })
        .catch(error => {
          console.error('Error when downloading logs:', error)
          this.requestCount--
        })
    },
    checkDefaultSite(site) {
      let defaultSite
      if (site && site !== 'all') {
        let found = this.getCurrentUser.sites.find(userSite => {
          return String(userSite.site) === site
        })
        if (found) {
          defaultSite = site
        } else {
          defaultSite = this.getCurrentUser.sites[0].site
        }
      } else {
        defaultSite = this.getCurrentUser.sites[0].site
      }

      return defaultSite
    },
  },
}
</script>

<style>
.starter-template {
  padding: 3rem 1.5rem;
  text-align: center;
}

.table > tbody > tr > td {
  vertical-align: middle;
}

.table > tbody > tr > th {
  vertical-align: middle;
}

@media screen and (max-width: 575px) {
  .tab-content .active {
    background-color: rgba(0, 0, 0, 0) !important;
  }
}
</style>
